import React, { Component } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import cn from 'classnames';

import {
  IMAGE,
  UPLOADED_BY_REVIEWER_ALT_TEXT,
  VIDEO
} from 'constants/appConstants';
import { formatDate, parseDate } from 'helpers/dateUtils';
import Rating from 'components/Rating';
import { ButtonSpinner } from 'components/Loader';
import ImageLazyLoader from 'components/common/ImageLazyLoader';
import FitSurvey from 'components/reviews/FitSurvey';
import { parseAndCleanHtmlToReact } from 'helpers/HtmlHelpers';
import { valueFromFitSurveyText } from 'helpers/ReviewUtils';

import css from 'styles/components/reviews/review.scss';

const makeRating = (shouldRender, value, label, dataTestId = '', additionalClasses = '') => (
  shouldRender && (
    <span data-test-id={dataTestId}>
      <em className={css.ratingLabel}>{label}</em>
      <Rating rating={value} additionalClasses={additionalClasses} />
    </span>
  )
);

const formatReviewDate = date => formatDate('MMMM D, YYYY', parseDate(date));

class Review extends Component {
  state = { isSummaryExpanded: false };

  onReadMoreClick = () => {
    this.setState({ isSummaryExpanded: !this.state.isSummaryExpanded });
  };

  makeBadges = () => {
    const {
      customerRewardReview, powerReview, premierReview, source, verifiedPurchase
    } = this.props.review;

    const { testId } = this.context;
    const showPowerReview = powerReview && source;
    return (
      <div className={css.badges}>
        {showPowerReview && <span className={css.badge} data-test-id={testId('powerReview')}>Reviewed at {source}</span>}
        {premierReview && (
          <span className={css.badge} data-test-id={testId('premierReview')}>
            Premier Review of Free Product <Link to="/c/premier-reviewers" className={css.whatsThis} data-test-id={testId('premierReviewLink')}>(What's this?)</Link>
          </span>
        )}
        {verifiedPurchase && <span className={css.badge} data-test-id={testId('verifiedPurchase')}>Verified Purchase</span>}
        {customerRewardReview && (
          <span className={css.badge} data-test-id={testId('reviewForRewards')}>
            Review for Zappos VIP Points <Link to="/c/vip-faqs" className={css.whatsThis} data-test-id={testId('reviewRewardsLink')}>(What's this?)</Link>
          </span>
        )}
      </div>
    );
  };

  makeFitSurvey = () => {
    const { shoeSize, shoeWidth, shoeArch } = this.props.review;
    // TODO process these in the reducer (see mweb/marty issue #6439)
    return <FitSurvey sizeRating={valueFromFitSurveyText(shoeSize)} widthRating={valueFromFitSurveyText(shoeWidth)} archRating={valueFromFitSurveyText(shoeArch)} />;
  };

  makeUpvoteButton = () => {
    const {
      props: {
        review: { id: reviewId },
        loadingReviews, onReviewUpvoteClick, submittedReviews
      },
      context: { testId }
    } = this;

    const isUpvoteLoading = loadingReviews.includes(reviewId);

    if (isUpvoteLoading) {
      return <ButtonSpinner size={14} className={css.spinner} />;
    }

    const isUpvoteSubmitted = submittedReviews.includes(reviewId);
    if (isUpvoteSubmitted) {
      return <p data-test-id={testId('reviewVotedText')}>Thank you for your feedback!</p>;
    }

    return (
      <button
        type="button"
        className={css.upvoteButton}
        onClick={onReviewUpvoteClick.bind(this, reviewId)}
        disabled={isUpvoteLoading}
        data-test-id={testId('helpfulVote')}>
        <span className={css.upvoteButtonIcon} /> Helpful?
      </button>
    );
  };

  makeNewReviewFooter = () => {
    const {
      props: {
        review: {
          powerReview: isPowerReviewString,
          formattedDate,
          reviewDate,
          name
        },
        shouldRenderStructuredData
      },
      context: { testId, marketplace: { reviews: { defaultReviewerName } } }
    } = this;

    const dateStructuredData = {};
    if (shouldRenderStructuredData) {
      dateStructuredData.itemProp = 'datePublished';
    }

    const isPowerReview = isPowerReviewString === true || isPowerReviewString === 'true';
    const displayDate = formattedDate || formatReviewDate(reviewDate);
    const upvotes = this.getUpvotes();
    return (
      <div className={css.footer}>
        <div>
          <span data-test-id={testId('reviewerName')}>{name || defaultReviewerName}{isPowerReview || ', Zappos Customer'}
          </span>, <span data-test-id={testId('publishedDate')} {...dateStructuredData}>{displayDate}</span>
          {shouldRenderStructuredData && <meta itemProp="author" content={name || defaultReviewerName } />}
        </div>
        <div className={css.upvoteButtonContainer} data-test-id={testId('helpfulVoteModal')}>
          {this.makeUpvoteButton()}
        </div>
        <div className={css.voteCountText}>
          <span className={css.voteCount} data-test-id={testId('upvoteCount')}>{upvotes}</span> found this review helpful.
        </div>
      </div>
    );
  };

  getUpvotes = () => {
    // TODO if your API is giving you booleans, numbers, etc. as strings,
    //      please convert them properly in a reducer.
    const { review: { upVotes } } = this.props;
    return parseInt(upVotes, 10);
  };

  makeReviewMediaItem = ({ mediaType, msaMediaUrl }) => {
    const {
      context: { testId }
    } = this;
    const itemProps = {
      className: css.mediaItem,
      src: msaMediaUrl
    };
    if (mediaType === IMAGE) {
      const loaderProps = {
        imgProps: {
          ...itemProps,
          'alt': UPLOADED_BY_REVIEWER_ALT_TEXT,
          'data-test-id': testId('reviewerMedia')
        },
        placeholder: <div />
      };
      return <ImageLazyLoader {...loaderProps} />;
    } else if (mediaType === VIDEO) {
      return <video {...itemProps} />;
    }
  };

  makeReviewMediaButton = (mediaItemData, index) => {
    const {
      props: { review: { id }, onReviewMediaClick },
      makeReviewMediaItem
    } = this;
    const mediaEl = makeReviewMediaItem(mediaItemData);
    const buttonProps = {
      key: mediaItemData.msaMediaUrl || mediaItemData.mediaUrl,
      className: css.mediaItemButton,
      onClick: onReviewMediaClick.bind(this, id, index)
    };
    return <button type="button" {...buttonProps}>{mediaEl}</button>;
  };

  makeReviewMedia = () => {
    const {
      props: {
        review: { reviewGalleryMedia },
        hiddenMediaItemIndex
      },
      makeReviewMediaButton
    } = this;
    if (reviewGalleryMedia && reviewGalleryMedia.length) {
      return reviewGalleryMedia.reduce((acc, mediaItemData, index) => {
        if (index !== hiddenMediaItemIndex) {
          acc.push(makeReviewMediaButton(mediaItemData, index));
        }
        return acc;
      }, []);
    } else {
      return null;
    }
  };

  render() {
    const {
      props: {
        review: {
          comfortRating, lookRating, otherShoes, overallRating, shoeArch,
          shoeSize, shoeWidth, summary
        },
        forceTabularRatings, limitSummaryHeight, productName,
        shouldRenderStructuredData, showFitSurvey, showMedia
      },
      context: { testId },
      state: { isSummaryExpanded },
      makeFitSurvey, makeBadges, onReadMoreClick
    } = this;

    const isSummaryLong = summary.length >= 200;
    const showSummaryExpander = limitSummaryHeight && isSummaryLong;
    const isExpanderHeightUnlimited = (!limitSummaryHeight) || (!isSummaryLong) || isSummaryExpanded;

    const containerStructuredData = {};
    const ratingStructuredData = {};
    const summaryStructuredData = {};
    if (shouldRenderStructuredData) {
      containerStructuredData.itemProp = 'review';
      containerStructuredData.itemScope = true;
      containerStructuredData.itemType = 'http://schema.org/Review';

      ratingStructuredData.itemProp = 'reviewRating';
      ratingStructuredData.itemScope = true;
      ratingStructuredData.itemType = 'http://schema.org/Rating';

      summaryStructuredData.itemProp = 'reviewBody';
    }

    return (
      <div
        className={cn(css.container, { [css.showFitSurvey]: showFitSurvey })}
        data-test-id={testId('reviewItem')}
        {...containerStructuredData}>
        {shouldRenderStructuredData && (
          <span itemProp="itemReviewed" itemScope itemType="http://schema.org/Product">
            <meta itemProp="name" content={productName}/>
          </span>
        )}
        <div>
          <div className={cn({ [css.opinionWithFitSurvey]: shoeSize || shoeArch || shoeWidth })}>
            <div className={css.ratings} {...ratingStructuredData}>
              <div className={cn(css.ratingWrapper, { [css.forceTabularRatings]: forceTabularRatings })}>
                {makeRating(true, overallRating, 'Overall', testId('overallStarRating'), cn(css.ratingLayout, { [css.forceTabularRatings]: forceTabularRatings }))}
                {shouldRenderStructuredData && <meta itemProp="ratingValue" content={overallRating}/>}
              </div>
              <div className={cn(css.ratingWrapper, { [css.forceTabularRatings]: forceTabularRatings })}>
                {makeRating(!!comfortRating, comfortRating, 'Comfort', testId('comfortStarRating'), cn(css.ratingLayout, { [css.forceTabularRatings]: forceTabularRatings }))}
              </div>
              <div className={cn(css.ratingWrapper, { [css.forceTabularRatings]: forceTabularRatings })}>
                {makeRating(!!lookRating, lookRating, 'Style', testId('styleStarRating'), cn(css.ratingLayout, { [css.forceTabularRatings]: forceTabularRatings }))}
              </div>
            </div>
            {makeBadges()}
            <div className={css.fitSurveyWrapper}>
              {showFitSurvey && makeFitSurvey()}
            </div>
            <div className={css.summary} data-test-id={testId('reviewBody')} {...summaryStructuredData}>
              <div className={cn(css.summaryHeightLimitWrapper, { [css.expanded]: isExpanderHeightUnlimited })} data-test-id={testId('reviewText')}>
                {parseAndCleanHtmlToReact(summary)}
              </div>
              {showSummaryExpander && (
                <button
                  type="button"
                  className={css.summaryExpander}
                  onClick={onReadMoreClick}
                  data-test-id={testId('reviewExpander')}>
                  {isSummaryExpanded ? 'Show Less' : 'Read More'}
                </button>
              )}
            </div>
            {/* Brands I'd Also Recommend (desktop-only) */}
            {otherShoes && <div className={css.recommendedBrands} data-test-id={testId('reviewerRecommendedBrands')}>
              <strong>I'd also recommend:</strong> {parseAndCleanHtmlToReact(otherShoes)}
            </div>}
            {this.makeNewReviewFooter()}
            {showMedia && (
              <div className={css.mediaContainer} data-test-id={testId('additionalReviewImages')}>
                {this.makeReviewMedia()}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

Review.defaultProps = {
  forceTabularRatings: false,
  hiddenMediaItemIndex: null,
  limitSummaryHeight: false,
  shouldRenderStructuredData: true,
  showFitSurvey: true,
  showMedia: true
};

Review.propTypes = {
  review: PropTypes.object.isRequired,
  productName: PropTypes.string.isRequired,
  forceTabularRatings: PropTypes.bool.isRequired,
  hiddenMediaItemIndex: PropTypes.number,
  limitSummaryHeight: PropTypes.bool.isRequired,
  shouldRenderStructuredData: PropTypes.bool.isRequired,
  showFitSurvey: PropTypes.bool.isRequired,
  showMedia: PropTypes.bool.isRequired,
  loadingReviews: PropTypes.array.isRequired,
  submittedReviews: PropTypes.array.isRequired,
  onReviewUpvoteClick: PropTypes.func.isRequired,
  onReviewMediaClick: PropTypes.func.isRequired
};

Review.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default Review;
